import React, { useState, useEffect } from 'react';
import { Box, Typography, Container } from '@mui/material';
import SEO from '../components/SEO/SEO';
import CommentList from '../components/forum/CommentList';
import CommentForm from '../components/forum/CommentForm';
import NameSelectionModal from '../components/forum/NameSelectionModal';
import { useUser } from '../context/UserContext';
import { useSigilContext } from '../context/sigilContext';

const Forum = () => {
    const { user, loading } = useUser();
    const [modalOpen, setModalOpen] = useState(false);
    const { i18n } = useSigilContext();
    const translate = i18n?.forum || {};

    useEffect(() => {
        if (!loading && !user) {
            setModalOpen(true);
        } else {
            setModalOpen(false);
        }
    }, [user, loading]);

    return (
        <>
            <SEO
                title={`${translate.title || 'Foro de la Comunidad'} | Magia del Caos`}
                description={translate.description || 'Comparte tus experiencias y sigilos con la comunidad.'}
                name="Magia del Caos"
                type="website"
            />

            <Container
                maxWidth="md"
                sx={{
                    paddingBottom: 4,
                    height: 'calc(100vh - 64px)', // Altura total menos la altura aproximada del Toolbar
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'rgba(255, 255, 255, 0.05)',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                    }
                }}
            >
                <Box sx={{ marginTop: 4, marginBottom: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'white', fontWeight: 'bold' }}>
                        {translate.title || 'Foro de la Comunidad'}
                    </Typography>

                    {/* Welcome Message (Pinned Header) */}
                    {translate.welcomeMessage && (
                        <Box sx={{
                            marginBottom: 4,
                            padding: 2.5,
                            border: '1px solid',
                            borderColor: 'secondary.main',
                            borderRadius: '18px',
                            backgroundColor: 'rgba(255, 215, 0, 0.05)',
                            position: 'relative',
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1.5 }}>
                                <Typography variant="caption" sx={{
                                    backgroundColor: 'secondary.main',
                                    color: 'primary.main',
                                    padding: '2px 10px',
                                    borderRadius: '4px',
                                    fontWeight: 'bold',
                                    marginRight: 1.5,
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    {translate.welcomeMessage.author || 'Admin'}
                                </Typography>
                                <Typography variant="caption" sx={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontWeight: 500
                                }}>
                                    ✨ Community Welcome
                                </Typography>
                            </Box>
                            <Typography variant="body1" sx={{
                                color: 'white',
                                fontStyle: 'italic',
                                textAlign: 'left',
                                lineHeight: 1.7,
                                fontSize: '1.05rem',
                                marginBottom: translate.rules ? 2 : 0
                            }}>
                                {translate.welcomeMessage.text}
                            </Typography>

                            {translate.rules && (
                                <Box sx={{ marginTop: 2, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 2 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 'bold', marginBottom: 1, textAlign: 'left' }}>
                                        {translate.rulesTitle}
                                    </Typography>
                                    <ul style={{ color: 'rgba(255,255,255,0.8)', textAlign: 'left', margin: 0, paddingLeft: '20px', fontSize: '0.9rem' }}>
                                        {translate.rules.map((rule, idx) => (
                                            <li key={idx} style={{ marginBottom: '4px' }}>{rule}</li>
                                        ))}
                                    </ul>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* New Comment Form */}
                    {user && (
                        <Box sx={{ marginBottom: 6 }}>
                            <CommentForm />
                        </Box>
                    )}

                    {/* Comments List */}
                    <CommentList />
                </Box>

                {/* Name Selection Modal */}
                <NameSelectionModal open={modalOpen} />
            </Container>
        </>
    );
};

export default Forum;
